import { isEmpty, isEmail } from '@jjj/components';
import actions from './actions';
import { updateCurrentTimezone } from '../../helpers/dateTime';
import { updateCurrentCountryContext, convertCountryContext } from '../../helpers/countriesHelpers';
import { createReducer } from '../../helpers/reducer';
import { trackUserLogin, trackUserLogout } from '../../helpers/googleAnalytics/events';
import { clearAuthData, getAuthData, setLocalStorageAuthData } from '../../helpers/auth';
import { THEME_CONFIG_KEY } from '../../helpers/localStorageKeys.const';
import { t } from '../../i18n';

export const steps = {
  START: null,
  LOGIN: 0,
  RESET_PASSWORD: 1,
  RESET_PASSWORD_EMAIL_SENT: 2,
  RESET_PASSWORD_CONFIRMATION: 3,
  RESET_PASSWORD_SUCCESS: 4,
  RESET_PASSWORD_TOKEN_EXPIRED: 5,
  RESET_PASSWORD_TOKEN_VALID: 6,
  CREATE_PASSWORD: 7,
  CREATE_PASSWORD_SUCCESS: 8,
  CREATE_PASSWORD_TOKEN_VALID: 9,
  CREATE_PASSWORD_TOKEN_EXPIRED: 10,
};

const initState = {
  authToken: null,
  isLoggedIn: false,
  id: null,
  code: null,
  createOrResetPasswordToken: null,
  loading: false,
  isCheckingLoginType: false,
  isCheckingLoginMethods: true,
  isChangeCountryLoading: false,
  isCheckingResetPasswordToken: false,
  isCheckingCreatePasswordToken: false,
  step: steps.START,
  profile: null,
  intercom: null,
  time_zone: undefined,
  countries: undefined,
  selectedCountry: undefined,
  hasError: false,
  navigation: null,
  loginMethods: null,
  email: {
    value: null,
    error: '',
    name: 'email',
    touched: false,
  },
};

function validate({ attr, value }) {
  let error = '';

  if (attr === 'email') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.signIn.username_label') });
    } else if (!isEmail(value)) {
      error = t('dash.validation.valid_error', { fieldName: t('dash.signIn.username_label') });
    }
  }

  return error;
}

function onChangeProperty(state, { payload }) {
  const { attr, value, step, touched, error } = payload;

  state[attr] = {
    value,
    error: error === '' ? error : validate({ attr, value, step, state }),
    name: attr,
    touched: touched === false ? touched : true,
  };

  return { ...state };
}

function onUpdateSelectedCountryRequest(state) {
  return { ...state, isChangeCountryLoading: true };
}

function onUpdateSelectedCountrySuccess(state, { payload: selectedCountry }) {
  updateCurrentCountryContext(selectedCountry);
  setLocalStorageAuthData(getAuthData());

  return {
    ...state,
    selectedCountry,
    isChangeCountryLoading: false,
  };
}

function onUpdateSelectedTimezone(state, { payload: time_zone }) {
  updateCurrentTimezone(time_zone);
  setLocalStorageAuthData(getAuthData());

  return {
    ...state,
    time_zone,
  };
}

function onLoginRequest(state) {
  return { ...state, loading: true, hasError: false };
}

function onLoginSuccess(state, { payload = {} }) {
  const { authToken, id, role, code, profile, time_zone, selectedCountry, countries, intercom } = payload;

  trackUserLogin({ profile, role });

  updateCurrentCountryContext(selectedCountry);
  updateCurrentTimezone(time_zone);

  return {
    ...state,
    authToken,
    isLoggedIn: !!id,
    id,
    role,
    code,
    profile,
    intercom,
    time_zone,
    countries,
    selectedCountry: convertCountryContext(selectedCountry),
    loading: false,
    hasError: false,
  };
}

function onUpdateProfileAvatar(state, { payload }) {
  return {
    ...state,
    profile: {
      ...state?.profile,
      avatar_url: payload?.avatarUrl,
    },
  };
}

function onUpdateProfile(state, { payload }) {
  return {
    ...state,
    profile: {
      ...payload.profile,
      name: payload.profile.first_name,
      lastName: payload.profile.last_name,
    },
  };
}

function onLoginError(state) {
  return {
    ...state,
    step: state.step,
    loginMethods: state.loginMethods,
    hasError: true,
    isCheckingLoginMethods: false,
    loading: false,
  };
}

function onGoogleRequest(state) {
  return { ...state, loading: true, hasError: false };
}

function onSetStep(state, { payload = { step: null } }) {
  return { ...state, step: payload.step };
}

function onCheckCreatePasswordTokenRequest(state) {
  return { ...state, loading: true, isCheckingCreatePasswordToken: true, step: steps.CREATE_PASSWORD };
}

function onCheckResetPasswordTokenRequest(state) {
  return { ...state, loading: true, isCheckingResetPasswordToken: true, step: steps.RESET_PASSWORD };
}

function onCheckCreatePasswordTokenSuccess(state, { payload = { token: null } }) {
  return {
    ...state,
    loading: false,
    isCheckingCreatePasswordToken: false,
    step: steps.CREATE_PASSWORD_TOKEN_VALID,
    createOrResetPasswordToken: payload.token,
  };
}

function onCheckCreatePasswordTokenFail(state) {
  return { ...state, loading: false, isCheckingCreatePasswordToken: false, step: steps.CREATE_PASSWORD_TOKEN_EXPIRED };
}

function onCheckResetPasswordTokenSuccess(state, { payload = { token: null } }) {
  return {
    ...state,
    loading: false,
    isCheckingResetPasswordToken: false,
    step: steps.RESET_PASSWORD_TOKEN_VALID,
    createOrResetPasswordToken: payload.token,
  };
}

function onCheckResetPasswordTokenFail(state) {
  return { ...state, loading: false, isCheckingResetPasswordToken: false, step: steps.RESET_PASSWORD_TOKEN_EXPIRED };
}

function onCreatePasswordRequest(state) {
  return { ...state, loading: true };
}

function onCreatePasswordSuccess(state) {
  return { ...state, loading: false, step: steps.CREATE_PASSWORD_SUCCESS };
}

function onCreatePasswordFail(state) {
  return { ...state, loading: false, step: steps.CREATE_PASSWORD_TOKEN_EXPIRED };
}

function onResetPasswordRequest(state) {
  return { ...state, loading: true };
}

function onResetPasswordSuccess(state) {
  return { ...state, loading: false, step: steps.RESET_PASSWORD_SUCCESS };
}

function onResetPasswordFail(state) {
  return { ...state, loading: false, step: steps.RESET_PASSWORD_TOKEN_EXPIRED };
}

function onSendConfirmationRequest(state) {
  return { ...state, loading: true };
}

function onSendConfirmationSuccess(state) {
  return { ...state, loading: false, step: steps.RESET_PASSWORD_EMAIL_SENT };
}

function onSendConfirmationFail(state) {
  return { ...state, loading: false, step: steps.RESET_PASSWORD_TOKEN_EXPIRED };
}

function onLogoutSuccess(state, { payload }) {
  if (payload?.inactivityReason && state.profile) {
    trackUserLogout(state.profile);
  }

  clearAuthData();
  sessionStorage.clear();
  localStorage.removeItem(THEME_CONFIG_KEY);

  return { ...initState, step: state.step, loginMethods: state.loginMethods, isCheckingLoginMethods: false };
}

function onCheckLoginTypeRequest(state) {
  return { ...state, isCheckingLoginType: true };
}

function onCheckLoginTypeSuccess(state, { payload }) {
  return { ...state, isCheckingLoginType: false, step: 0, navigation: payload.navigation };
}

function onCheckLoginTypeError(state, { payload }) {
  return {
    ...state,
    isCheckingLoginType: false,
    email: { value: state.email.value, error: payload?.error, touched: true },
  };
}

function onCheckLoginMethodsRequest(state) {
  return { ...state, hasError: false, isCheckingLoginMethods: true };
}

function onCheckLoginMethodsSuccess(state, { payload }) {
  const { login_methods: loginMethods } = payload;

  return { ...state, isCheckingLoginMethods: false, loginMethods };
}

function onCheckLoginMethodsError(state) {
  return {
    ...state,
    isCheckingLoginMethods: false,
    hasError: true,
  };
}

function onLogoutFromUnifiedLogin(state) {
  return { ...state, loading: true };
}

function onLogoutFromUnifiedLoginSuccess(state) {
  clearAuthData();
  sessionStorage.clear();
  localStorage.removeItem(THEME_CONFIG_KEY);

  return { ...state, loading: false };
}

function onLogoutFromUnifiedLoginError(state) {
  return { ...state, loading: false };
}

function onClearError(state) {
  return { ...state, hasError: false };
}

function onCheckAuthorizationSuccess(state) {
  return { ...state };
}

export default createReducer(initState, {
  [actions.CHANGE_PROPERTY]: onChangeProperty,
  [actions.CHECK_CREATE_PASSWORD_TOKEN_FAIL]: onCheckCreatePasswordTokenFail,
  [actions.CHECK_CREATE_PASSWORD_TOKEN_REQUEST]: onCheckCreatePasswordTokenRequest,
  [actions.CHECK_CREATE_PASSWORD_TOKEN_SUCCESS]: onCheckCreatePasswordTokenSuccess,
  [actions.CHECK_RESET_PASSWORD_TOKEN_FAIL]: onCheckResetPasswordTokenFail,
  [actions.CHECK_RESET_PASSWORD_TOKEN_REQUEST]: onCheckResetPasswordTokenRequest,
  [actions.CHECK_RESET_PASSWORD_TOKEN_SUCCESS]: onCheckResetPasswordTokenSuccess,
  [actions.CREATE_PASSWORD_FAIL]: onCreatePasswordFail,
  [actions.CREATE_PASSWORD_REQUEST]: onCreatePasswordRequest,
  [actions.CREATE_PASSWORD_SUCCESS]: onCreatePasswordSuccess,
  [actions.GOOGLE_REQUEST]: onGoogleRequest,
  [actions.LOGIN_ERROR]: onLoginError,
  [actions.LOGIN_REQUEST]: onLoginRequest,
  [actions.LOGIN_SUCCESS]: onLoginSuccess,
  [actions.LOGOUT_SUCCESS]: onLogoutSuccess,
  [actions.RESET_PASSWORD_FAIL]: onResetPasswordFail,
  [actions.RESET_PASSWORD_REQUEST]: onResetPasswordRequest,
  [actions.RESET_PASSWORD_SUCCESS]: onResetPasswordSuccess,
  [actions.SEND_CONFIRMATION_FAIL]: onSendConfirmationFail,
  [actions.SEND_CONFIRMATION_REQUEST]: onSendConfirmationRequest,
  [actions.SEND_CONFIRMATION_SUCCESS]: onSendConfirmationSuccess,
  [actions.SET_STEP]: onSetStep,
  [actions.UPDATE_SELECTED_COUNTRY_REQUEST]: onUpdateSelectedCountryRequest,
  [actions.UPDATE_SELECTED_COUNTRY_SUCCESS]: onUpdateSelectedCountrySuccess,
  [actions.UPDATE_SELECTED_TIMEZONE]: onUpdateSelectedTimezone,
  [actions.CLEAR_ERROR]: onClearError,
  [actions.UPDATE_PROFILE_AVATAR]: onUpdateProfileAvatar,
  [actions.UPDATE_PROFILE]: onUpdateProfile,
  [actions.CHECK_LOGIN_TYPE_REQUEST]: onCheckLoginTypeRequest,
  [actions.CHECK_LOGIN_TYPE_SUCCESS]: onCheckLoginTypeSuccess,
  [actions.CHECK_LOGIN_TYPE_ERROR]: onCheckLoginTypeError,
  [actions.CHECK_LOGIN_METHODS_REQUEST]: onCheckLoginMethodsRequest,
  [actions.CHECK_LOGIN_METHODS_SUCCESS]: onCheckLoginMethodsSuccess,
  [actions.CHECK_LOGIN_METHODS_ERROR]: onCheckLoginMethodsError,
  [actions.LOGOUT_FROM_UNIFIED_LOGIN]: onLogoutFromUnifiedLogin,
  [actions.LOGOUT_FROM_UNIFIED_LOGIN_SUCCESS]: onLogoutFromUnifiedLoginSuccess,
  [actions.LOGOUT_FROM_UNIFIED_LOGIN_ERROR]: onLogoutFromUnifiedLoginError,
  [actions.CHECK_AUTHORIZATION_SUCCESS]: onCheckAuthorizationSuccess,
});
